import { IonContent, IonPage } from '@ionic/react';
import React from 'react';
import { dashboardPageStyle } from '../../Layout';
import PageTitle from '../../../../components/Dashboard/PageTitle';
import InventoryOverviewCard, {
  InventoryOverviewCardSkeleton,
} from '../../../../components/Dashboard/inventory/InventoryOverviewCard';
import InventoryTable, {
  InventoryTableSkeleton,
} from '../../../../components/Dashboard/inventory/InventoryTable';
import useStoreInventoryService from '../../../../services/inventory/hooks/useStoreInventoryService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import EmptyPage from '../../../../components/ui/EmptyPage';
import { IonicButton } from 'ionic-ui';
import { useDialog } from '../../../../context/Dialog';
import AddNewProductDialog from '../../../../components/Dashboard/catalog/AddNewProductDialog/AddNewProductDialog';
import { Link } from 'react-router-dom';

export default function index() {
  const url = new URLSearchParams(window.location.search);
  const currentPage = url.get('page') ? Number(url.get('page')) : 1;
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const { activeStore } = useSelector((state: RootState) => state.userStores);
  const storeId = activeStore?.id;

  const { inventory, metrics } = useStoreInventoryService({
    authToken,
    storeId,
    currentPage,
    target: 'both',
  });

  const { openDialog } = useDialog();
  const handleNewProduct = () => {
    openDialog(<AddNewProductDialog />, { allowBackgroundDismiss: false });
  };

  const { hasStore } = useSelector((state: RootState) => state.userStores);

  const inventoryAnalytics = React.useMemo(() => {
    if (!metrics?.data) return [0, 0, 0];
    const { sales, revenue } = metrics.data;
    return [
      { ...sales, title: 'sales' },
      { ...revenue, title: 'revenue' },
    ];
  }, [metrics]);

  return (
    <IonPage>
      <IonContent>
        <div className={dashboardPageStyle}>
          <div className="space-y-2">
            <PageTitle>Inventory Analytics</PageTitle>
            <p className="medium-emphasis-text">
              📊 You're managing {metrics.data?.products_count || 0} active
              products. Keep an eye on low stock items to avoid missed orders.
            </p>
          </div>

          <>
            {!hasStore ? (
              <EmptyPage
                message="It looks like you haven't created a store yet."
                className=" h-[50vh]"
              >
                <Link
                  to="/dashboard/get-started"
                  className="text-primary w-fit mx-auto p-4 font-medium"
                >
                  Create Store
                </Link>
              </EmptyPage>
            ) : (
              <>
                {metrics.isError ? (
                  <EmptyPage
                    message="Something went wrong, try again."
                    className="h-[30vh]"
                  >
                    <IonicButton
                      onClick={metrics.refetch}
                      variant="ghost"
                      className="high-emphasis-text w-fit mx-auto"
                    >
                      Try Again
                    </IonicButton>
                  </EmptyPage>
                ) : (
                  <div className="flex flex-col sm:flex-row items-stretch flex-wrap w-full gap-4">
                    {inventoryAnalytics.map((data, index) => (
                      <div className="flex-1 sm:min-w-[250px]" key={index}>
                        {metrics.isLoading || metrics.isPending ? (
                          <InventoryOverviewCardSkeleton />
                        ) : (
                          <InventoryOverviewCard
                            title={data.title}
                            currentValue={data.current_period}
                            previousValue={data.previous_period}
                            change={data.change_percentage}
                            currentPeriod={
                              metrics?.data?.period?.current || undefined
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* TAB SECTION */}
                {/* <div className='bg-gray-50 w-full h-fit rounded-lg'>
                        <TabSwitch tabs={tabs} className='!border-none' activeTab={activeTab} onChange={(selectedTab)=>{setActiveTab(selectedTab)}} />
                    </div> */}
                {inventory?.isLoading || inventory?.isPending ? (
                  <InventoryTableSkeleton />
                ) : inventory.isError || !inventory.data ? (
                  <EmptyPage
                    message="Something went wrong, try again."
                    className="h-[40vh]"
                  >
                    <IonicButton
                      onClick={inventory.refetch}
                      variant="ghost"
                      className="high-emphasis-text w-fit mx-auto"
                    >
                      Try Again
                    </IonicButton>
                  </EmptyPage>
                ) : inventory.data && !inventory.data?.data.length ? (
                  <EmptyPage
                    message="No Products in catalog"
                    className="sm:col-span-2 md:col-span-3 h-[40vh]"
                  >
                    <IonicButton
                      onClick={handleNewProduct}
                      variant="ghost"
                      size="lg"
                    >
                      Add New Product
                    </IonicButton>
                  </EmptyPage>
                ) : (
                  <InventoryTable
                    tableData={inventory?.data?.data}
                    totalPages={inventory?.data.pagination.total_pages}
                  />
                )}
              </>
            )}
          </>
        </div>
      </IonContent>
    </IonPage>
  );
}
